
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  Ref,
  watch,
  ref,
} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { ContentComment, Course } from "../../../domain/entities/entities";
import { RepositoryException } from "../../../domain/exceptions/exceptions";
import { dispatchErrorBox, toLocaleDateTime } from "../components/mixins";
import { wrapLoading } from "../utils/wrap_loading";
import { getOrderFromURL } from "../utils/order_utils";
import { updateQuery } from "../utils/routing_utils";
import { dispatchError } from "../utils/error_handling";
import course from "../components/application/Course.vue";
import CommentsModal from "../components/application/DashboardComments/CommentsModal.vue";
import ModalIntroductoryContent from "../components/application/ModalIntroductoryContent.vue";
import Comment from "../components/application/DashboardComments/Comment.vue";
import ContentFallback from "../components/application/moldure/ContentFallback.vue";
import PaginationDefault from "../components/application/shop/PaginationDefault.vue";
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel";
import SearchHeader from "../components/application/dashboard/SearchHeader.vue";

export default defineComponent({
  methods: { updateQuery },
  components: {
    Carousel,
    Slide,
    Navigation,
    Pagination,
    course,
    PaginationDefault,
    CommentsModal,
    ContentFallback,
    ModalIntroductoryContent,
    Comment,
    SearchHeader,
  },
  props: {
    entityID: {
      type: String,
    },
  },

  setup(props) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const commentModalTitle = ref("");

    const course = ref(new Course());
    const openModalIntroductoryContent: Ref<boolean> = ref(false);

    const courses: ComputedRef<Course[]> = computed(
      () => store.getters["course_module/coursesByCourseTrack"]
    );

    const coursesPage: ComputedRef<number> = computed(
      () => store.getters["course_module/coursesByCourseTrackPage"]
    );

    const comments: ComputedRef<ContentComment[]> = computed(
      () => store.getters["course_module/comments"]
    );

    const isCommentsModalOpen: ComputedRef<boolean> = computed(
      () => store.getters["course_module/isCommentsModalOpen"]
    );

    onBeforeMount(async () => {
      await loadCourses();
    });

    async function loadCourses(): Promise<void> {
      try {
        const order = getOrderFromURL(route);
        await wrapLoading(async () => {
          await store.dispatch("course_module/loadCoursesOfCourseTrack", {
            order: order,
            courseTrackID: props.entityID,
          });
        });
      } catch (e) {
        await dispatchError(e);
      }
    }

    const enableBodyOverflow = () => {
      document.body.style.overflow = "auto";
    };

    const disableBodyOverflow = () => {
      document.body.style.overflow = "hidden";
    };

    const openCommentsModal = async () => {
      disableBodyOverflow();
      await store.dispatch("course_module/openCommentsModal");
    };

    const closeCommentsModal = async () => {
      enableBodyOverflow();
      await store.dispatch("course_module/closeCommentsModal");
      await store.dispatch("course_module/clearComments");
    };

    const loadContentCommentsById = async (course: Course) =>
      await store.dispatch("course_module/loadContentCommentsById", course.id);

    const loadCourseCommentsModal = async (course: Course) => {
      try {
        commentModalTitle.value = course.title;
        await openCommentsModal();
        await loadContentCommentsById(course);
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    };

    const openIntroductoryContent = (_course: Course): void => {
      course.value = _course;
      openModalIntroductoryContent.value = true;
    };

    const closeModalIntroductoryContent = (): void => {
      openModalIntroductoryContent.value = false;
    };

    const openCourse = (course: Course) => {
      router.push({ name: "course", params: { course_id: course.id } });
    };

    watch(
      () => route.query.page,
      async () => {
        if (route.name === "details_courses") {
          await loadCourses();
        }
      }
    );

    return {
      courses,
      comments,
      isCommentsModalOpen,
      commentModalTitle,
      course,
      openModalIntroductoryContent,
      coursesPage,
      toLocaleDateTime,
      closeCommentsModal,
      openCommentsModal,
      loadCourseCommentsModal,
      openIntroductoryContent,
      closeModalIntroductoryContent,
      openCourse,
    };
  },
});
